import merge from 'lodash-es/merge';
import { spacing } from './common';
import createCustomTheme from './createCustomTheme';
import { MuiRadioCheckedIcon, MuiRadioIcon } from './defaultProps';
import { fonts } from './fonts';
import { palette as paletteBase, paletteDark } from './palette';
const disableRipple = true;
const borderRadius = 0;
const buttonStyles = Object.assign({ borderRadius, boxShadow: 'none', textTransform: 'none', padding: `${spacing(1.5)}px ${spacing(3)}px` }, fonts.button);
export const buttonPressedBackgroundColor = '#234B66';
const dividerStyles = {
    height: 0,
    borderTop: `1px solid ${paletteBase.grey[400]}`,
    borderBottom: '1px solid white',
};
const sliderBoxShadow = '0 0 3px 0 rgba(0, 0, 0, 0.42)';
const TOOLTIP_TOGGLE_DELAY = 650;
const baseTheme = (mode, palette, overrideOptions = {}) => {
    const paletteColors = mode === 'light' ? palette : paletteDark;
    const baseOptions = {
        palette: Object.assign({ mode }, paletteColors),
        typography: Object.assign({}, fonts),
        components: {
            MuiDivider: {
                defaultProps: {
                    light: true,
                },
                styleOverrides: {
                    root: dividerStyles,
                    light: {
                        borderBottomWidth: 0,
                        borderTop: `1px solid ${paletteBase.grey[200]}`,
                    },
                },
            },
            MuiRadio: {
                defaultProps: {
                    icon: MuiRadioIcon,
                    checkedIcon: MuiRadioCheckedIcon,
                },
                styleOverrides: {
                    root: {
                        padding: spacing(0.75),
                    },
                },
            },
            MuiButtonBase: {
                defaultProps: {
                    disableRipple,
                    style: {
                        borderRadius,
                    },
                },
            },
            MuiButton: {
                defaultProps: {
                    style: Object.assign({}, buttonStyles),
                },
                styleOverrides: {
                    root: {
                        borderRadius,
                    },
                    containedPrimary: {
                        '&:hover': {
                            backgroundColor: paletteBase.grey[700],
                        },
                        '&:active': {
                            backgroundColor: buttonPressedBackgroundColor,
                        },
                        '&[disabled]': {
                            backgroundColor: paletteBase.grey[500],
                            color: 'white',
                        },
                    },
                    containedSecondary: {
                        backgroundColor: paletteBase.grey[50],
                        color: paletteBase.primary.main,
                        '&:hover': {
                            backgroundColor: paletteBase.grey[700],
                            color: 'white',
                        },
                        '&:active': {
                            backgroundColor: buttonPressedBackgroundColor,
                        },
                        '&[disabled]': {
                            backgroundColor: paletteBase.grey[50],
                        },
                    },
                    outlinedSecondary: {
                        border: '1.5px solid',
                        borderColor: paletteBase.primary.main,
                        color: paletteBase.primary.main,
                        '&:hover': {
                            backgroundColor: paletteBase.grey[700],
                            border: '1.5px solid',
                            borderColor: paletteBase.grey[800],
                            color: 'white',
                        },
                        '&:focus': {
                            backgroundColor: paletteBase.grey[100],
                            color: paletteBase.primary.main,
                        },
                        '&:active': {
                            backgroundColor: buttonPressedBackgroundColor,
                            color: 'white',
                        },
                        '&[disabled]': {
                            borderColor: paletteBase.grey[400],
                            color: paletteBase.grey[400],
                        },
                    },
                },
            },
            MuiTooltip: {
                defaultProps: {
                    enterDelay: TOOLTIP_TOGGLE_DELAY,
                },
                styleOverrides: {
                    tooltip: {
                        color: 'white',
                        backgroundColor: paletteBase.primary.main,
                        fontSize: '12px',
                        maxWidth: spacing(20),
                        margin: 0,
                        borderRadius: 0,
                        padding: spacing(1),
                    },
                },
            },
            MuiFormGroup: {
                styleOverrides: {
                    root: {
                        '&[role="radiogroup"]': {
                            paddingLeft: spacing(0.75),
                        },
                    },
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    input: {
                        '&::placeholder': {
                            fontStyle: 'italic',
                        },
                        '&::-ms-input-placeholder': {
                            fontStyle: 'italic',
                        },
                    },
                },
            },
            MuiSvgIcon: {
                styleOverrides: {
                    colorPrimary: {
                        color: `${paletteBase.primary.main} !important`,
                    },
                },
            },
            MuiLinearProgress: {
                styleOverrides: {
                    root: {
                        height: 8,
                    },
                    colorPrimary: {
                        backgroundColor: paletteBase.grey[200],
                    },
                    bar1Determinate: {
                        background: `repeating-linear-gradient(
                            135deg,
                            ${paletteBase.primary.main},
                            ${paletteBase.primary.main} 4px,
                            ${paletteBase.primary.light} 4px,
                            ${paletteBase.primary.light} 8px
                        )`,
                    },
                },
            },
            MuiPopover: {
                styleOverrides: {
                    paper: {
                        borderRadius,
                    },
                },
            },
            MuiSlider: {
                styleOverrides: {
                    root: {
                        color: paletteBase.grey[400],
                        height: 8,
                        display: 'flex',
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                    },
                    thumb: {
                        boxShadow: sliderBoxShadow,
                        height: 12,
                        width: 12,
                        backgroundImage: `linear-gradient(180deg, ${paletteBase.grey[50]} 0%, ${paletteBase.grey[400]} 100%)`,
                        '&:hover': { boxShadow: sliderBoxShadow },
                        '&:focus': { boxShadow: sliderBoxShadow },
                    },
                    rail: {
                        opacity: 0.5,
                        height: 4,
                        borderRadius: 3,
                    },
                    track: {
                        height: 4,
                        borderRadius: 3,
                        boxShadow: '0 1px 0 0 rgba(0, 0, 0, 0.12)',
                    },
                    thumbColorPrimary: {
                        backgroundImage: `linear-gradient(180deg, ${paletteBase.primary.main} 0%, #234B66 100%)`,
                    },
                    thumbColorSecondary: {
                        '&:hover': { boxShadow: sliderBoxShadow },
                        '&:focus': { boxShadow: sliderBoxShadow },
                        backgroundImage: `linear-gradient(180deg, ${paletteBase.grey[50]} 0%, ${paletteBase.grey[400]} 100%)`,
                    },
                    colorPrimary: {
                        '& span:first-of-type': {
                            backgroundColor: paletteBase.grey[400],
                        },
                        '& span:nth-of-type(2)': {
                            color: paletteBase.primary.main,
                            backgroundImage: 'none',
                        },
                    },
                    colorSecondary: {
                        '& span:first-of-type': {
                            backgroundColor: paletteBase.secondary.main,
                        },
                        '& span:nth-of-type(2)': {
                            backgroundImage: `linear-gradient(180deg, ${paletteBase.grey[50]} 0%, ${paletteBase.grey[400]} 100%)`,
                        },
                    },
                },
            },
            MuiSelect: {
                defaultProps: {
                    variant: 'standard',
                },
            },
            MuiTextField: {
                defaultProps: {
                    variant: 'standard',
                },
            },
            MuiFormControl: {
                defaultProps: {
                    variant: 'standard',
                },
            },
            MuiLink: {
                defaultProps: {
                    underline: 'hover',
                },
            },
        },
    };
    return merge(baseOptions, overrideOptions);
};
export const getTheme = (paletteMode, overrideOptions = {}) => createCustomTheme(baseTheme(paletteMode, paletteBase, overrideOptions));
export const darkTheme = getTheme('dark');
export const theme = getTheme('light');
//# sourceMappingURL=theme.js.map